// Contracts — kids per-moderator personal landing editor (#102, 2026-07-04). An
// admin (own office) / manager (any office) selects a moderator and edits THAT
// moderator's personal Remote-landing copy: the CMS content blocks (mirrors
// office_content) + a set of consent-block PDF documents (different countries carry
// their own paperwork). Backed by mod_landing_content / mod_landing_files (0019).
//
// The intake "ссылка заполнения" already attributes to one moderator via ?ref=<pid>;
// the public landing that link opens now serves that moderator's blocks (fallback
// office) + their consent PDFs. This file covers the ADMIN edit surface; the public
// render shape lives in ./landing.ts (KidsPublicOfficeRes.docs / blockSource).
import { z } from 'zod';

// ── GET /api/kids/admin/mod-landing?moderator=<userId> ───────────────────────
export const KidsModLandingQuerySchema = z.object({
  moderator: z.string().trim().min(1).max(80),
});
export type KidsModLandingQuery = z.infer<typeof KidsModLandingQuerySchema>;

// A single content block. `source` marks whether the value comes from the
// moderator's own override row ('moderator') or the office default used to
// pre-fill an unset block ('office') — the editor shows a hint and, on save,
// writes every block as a moderator override.
export const KidsModLandingBlockSchema = z.object({
  blockKey: z.string(),
  content: z.string(),
  source: z.enum(['office', 'moderator']),
  updatedAt: z.number().int().nullable(),
});
export type KidsModLandingBlock = z.infer<typeof KidsModLandingBlockSchema>;

export const KidsModLandingFileSchema = z.object({
  id: z.string(),
  filename: z.string(),
  createdAt: z.number().int(),
});
export type KidsModLandingFile = z.infer<typeof KidsModLandingFileSchema>;

export const KidsModLandingResSchema = z.object({
  moderator: z.string(),
  office: z.string().nullable(),
  blocks: z.array(KidsModLandingBlockSchema),
  files: z.array(KidsModLandingFileSchema),
});
export type KidsModLandingRes = z.infer<typeof KidsModLandingResSchema>;

// ── PUT /api/kids/admin/mod-landing → upsert one or more blocks ──────────────
export const KidsModLandingUpdateReqSchema = z.object({
  moderator: z.string().trim().min(1).max(80),
  blocks: z
    .array(
      z.object({
        blockKey: z.string().trim().min(1).max(80),
        content: z.string().max(20000),
      }),
    )
    .min(1)
    .max(40),
});
export type KidsModLandingUpdateReq = z.infer<typeof KidsModLandingUpdateReqSchema>;

export const KidsModLandingUpdateResSchema = z.object({ ok: z.literal(true) });
export type KidsModLandingUpdateRes = z.infer<typeof KidsModLandingUpdateResSchema>;

// ── POST /api/kids/admin/mod-landing-pdf → presigned PUT for a consent PDF ────
// Mirrors pack/presign: the browser gets a short-lived signed PUT URL and streams
// the PDF straight to R2 (ICF bucket), then the row is already recorded server-side.
// filename is the human name shown on the landing; contentType is fixed to PDF.
export const KidsModLandingPdfReqSchema = z.object({
  moderator: z.string().trim().min(1).max(80),
  filename: z.string().trim().min(1).max(200),
  contentType: z.literal('application/pdf').default('application/pdf'),
});
export type KidsModLandingPdfReq = z.infer<typeof KidsModLandingPdfReqSchema>;

export const KidsModLandingPdfResSchema = z.object({
  id: z.string(),
  url: z.string(), // short-lived signed PUT URL (browser → R2)
  expiresAt: z.number().int(),
});
export type KidsModLandingPdfRes = z.infer<typeof KidsModLandingPdfResSchema>;

// ── DELETE /api/kids/admin/mod-landing-pdf → remove a document ───────────────
export const KidsModLandingPdfDeleteReqSchema = z.object({
  moderator: z.string().trim().min(1).max(80),
  id: z.string().trim().min(1).max(80),
});
export type KidsModLandingPdfDeleteReq = z.infer<typeof KidsModLandingPdfDeleteReqSchema>;

export const KidsModLandingPdfDeleteResSchema = z.object({ ok: z.literal(true) });
export type KidsModLandingPdfDeleteRes = z.infer<typeof KidsModLandingPdfDeleteResSchema>;
